import { useMemo } from 'react';
import styled from 'styled-components';

import { getWorkflowTriggerResourceName } from 'modules/workflowIndex/utils/getWorkflowTriggerResourceName';
import { colors, fontSizes } from 'ui/theme';
import { BottomTooltip } from 'ui/tooltip';
import { pxToRem } from 'utils/convert';

import { WORKFLOW_TRIGGER_EVENTS, WORKFLOW_TRIGGER_EVENT_DISPLAY_NAMES } from '../../constants';
import { WorkflowStartNodeData } from '../../types';

import { TableCell } from './TableCell';

type WorkflowTriggerCellProps = {
  workflow: WorkflowStartNodeData;
};

const TriggerInfo = styled.div`
  width: 100%;
`;

const TriggerName = styled.div`
  color: ${colors.textPrimary};
  font-size: ${fontSizes.s14};
`;

const TriggerResourceName = styled.div`
  color: ${colors.textSecondary};
  font-size: ${fontSizes.s12};
  word-wrap: break-word;
`;

const TriggerTooltipContent = styled.div`
  max-width: ${pxToRem(232)};
  overflow-wrap: break-word;
  word-wrap: break-word;
`;

const getTriggerDisplayText = (workflow: WorkflowStartNodeData) => {
  if (workflow.startNode) {
    const startNodeTypename = workflow.startNode.nodeData.__typename;
    const triggerEventKey = startNodeTypename.endsWith('Trigger')
      ? startNodeTypename.slice(0, startNodeTypename.length - 7)
      : startNodeTypename;
    if (triggerEventKey in WORKFLOW_TRIGGER_EVENT_DISPLAY_NAMES) {
      return WORKFLOW_TRIGGER_EVENT_DISPLAY_NAMES[triggerEventKey as WORKFLOW_TRIGGER_EVENTS];
    }
  }
  return null;
};

export const WorkflowTriggerCell = ({ workflow }: WorkflowTriggerCellProps) => {
  const { resourceName, resourceTooltip } = useMemo(
    () => getWorkflowTriggerResourceName(workflow),
    [workflow],
  );

  return (
    <TableCell data-test="workflow-trigger-cell">
      <TriggerInfo>
        <TriggerName>{getTriggerDisplayText(workflow)}</TriggerName>
        {resourceName && (
          <TriggerResourceName data-test="workflow-resource-name">
            {resourceTooltip ? (
              <>
                {resourceName},{' '}
                <BottomTooltip
                  isUnderlined
                  tooltipContent={
                    <TriggerTooltipContent>{resourceTooltip.text}</TriggerTooltipContent>
                  }
                >
                  {resourceTooltip.triggerText}
                </BottomTooltip>
              </>
            ) : (
              resourceName
            )}
          </TriggerResourceName>
        )}
      </TriggerInfo>
    </TableCell>
  );
};
